// client/src/components/chat/ChatDrawer.js
import { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import Chat from './Chat'
import './ChatDrawer.css'

/**
 * ChatDrawer Component
 *
 * Sliding drawer overlay that displays the Chat component.
 * Supports focused mode for displaying specific channels.
 *
 * @param {boolean} isOpen - Whether drawer is initially open
 * @param {function} onClose - Callback when drawer is closed
 * @param {function} onOpen - Callback when drawer is opened (e.g., via FAB click)
 * @param {string|number} channelId - Optional channel ID to focus on
 * @param {boolean} hideSidebar - Whether to hide the channel sidebar
 * @param {string|number} divisionId - Optional division ID to filter channels
 * @param {string|number} leagueId - Optional league ID to include league chat
 * @param {string|number} initialChannelId - Optional channel ID to auto-select
 */
const ChatDrawer = forwardRef(
  (
    {
      isOpen: initialIsOpen = false,
      onClose,
      onOpen,
      channelId = null,
      hideSidebar = false,
      divisionId = null,
      leagueId = null,
      initialChannelId = null,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(initialIsOpen)

    // Allow parent to control open state if needed, or expose methods
    useImperativeHandle(ref, () => ({
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      toggle: () => setIsOpen((prev) => !prev),
    }))

    useEffect(() => {
      if (initialIsOpen !== undefined) {
        setIsOpen(initialIsOpen)
      }
    }, [initialIsOpen])

    // Lock body scroll when drawer is open on mobile
    useEffect(() => {
      if (isOpen) {
        // Save current scroll position
        const scrollY = window.scrollY
        document.body.classList.add('chat-drawer-open')
        document.body.style.top = `-${scrollY}px`
      } else {
        // Restore scroll position
        const scrollY = document.body.style.top
        document.body.classList.remove('chat-drawer-open')
        document.body.style.top = ''
        if (scrollY) {
          window.scrollTo(0, parseInt(scrollY || '0', 10) * -1)
        }
      }

      return () => {
        document.body.classList.remove('chat-drawer-open')
        document.body.style.top = ''
      }
    }, [isOpen])

    const handleClose = () => {
      setIsOpen(false)
      if (onClose) onClose()
    }

    const handleOpen = () => {
      if (onOpen) {
        // Let parent control the state if onOpen callback is provided
        onOpen()
      } else {
        // Otherwise, manage internal state
        setIsOpen(true)
      }
    }

    return (
      <>
        {/* Backdrop */}
        <div
          className={`chat-drawer-backdrop ${isOpen ? 'open' : ''}`}
          onClick={handleClose}
        />

        {/* Drawer */}
        <div
          className={`chat-drawer ${isOpen ? 'open' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="chat-drawer-title"
        >
          <div className="chat-drawer-header">
            <h3 id="chat-drawer-title" className="chat-drawer-title">
              Messages
            </h3>
            <button
              className="chat-drawer-close"
              onClick={handleClose}
              aria-label="Close chat drawer"
            >
              ✕
            </button>
          </div>
          <div className="chat-drawer-content">
            {/* Only render Chat when drawer is open (or keep it mounted but hidden if we want to preserve state) */}
            {/* Keeping it mounted allows socket connection to persist */}
            <Chat
              channelId={channelId}
              hideSidebar={hideSidebar}
              divisionId={divisionId}
              leagueId={leagueId}
              initialChannelId={initialChannelId}
            />
          </div>
        </div>

        {/* Floating Action Button (FAB) */}
        {!isOpen && (
          <button className="chat-fab" onClick={handleOpen} title="Open Chat">
            <span className="chat-fab-icon">💬</span>
          </button>
        )}
      </>
    )
  }
)

ChatDrawer.displayName = 'ChatDrawer'

ChatDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  channelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hideSidebar: PropTypes.bool,
  divisionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  leagueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialChannelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

export default ChatDrawer
